import { TimeSlot } from "types/time-slot";
import { WebRule } from "types/web-rule";

/**
 * Checks if a time slot is bookable for the given guest count
 */
const isTimeBookable = (time: TimeSlot, guestCount: number): boolean => {
    return time.availableSeats.includes(guestCount);
};

/**
 * Checks if a time slot is available for waitlist for the given guest count
 */
const isTimeWaitListable = (time: TimeSlot, guestCount: number): boolean => {
    return time?.waitListSeats?.includes(guestCount) ?? false;
};

/**
 * Filters time slots to only include those that are bookable for the given guest count
 */
const getBookableTimes = (times: TimeSlot[], guestCount: number): TimeSlot[] => {
    return times.filter((time) => isTimeBookable(time, guestCount));
};

/**
 * Filters time slots to only include those that are waitlistable for the given guest count
 */
const getWaitListTimes = (times: TimeSlot[], guestCount: number): TimeSlot[] => {
    return times.filter((time) => isTimeWaitListable(time, guestCount));
};

/**
 * Filters a time set to only include bookable times for the given guest count
 */
const getBookableTimeSet = (timeSet: WebRule, guestCount: number): WebRule => {
    return {
        ...timeSet,
        times: getBookableTimes(timeSet.times, guestCount),
    } as WebRule;
};

/**
 * Filters a time set to only include waitlistable times for the given guest count
 */
const getWaitListTimeSet = (timeSet: WebRule, guestCount: number): WebRule => {
    return {
        ...timeSet,
        times: getWaitListTimes(timeSet.times, guestCount),
    } as WebRule;
};

/**
 * Filters multiple time sets to only include bookable times for the given guest count
 * Removes any time sets that end up with no times
 */
const getBookableTimeSets = (timeSets: WebRule[], guestCount: number): WebRule[] => {
    return timeSets
        .map((timeSet) => getBookableTimeSet(timeSet, guestCount))
        .filter((timeSet) => timeSet.times.length > 0);
};

/**
 * Filters multiple time sets to only include waitlistable times for the given guest count
 * Removes any time sets that end up with no times
 */
const getWaitListTimeSets = (timeSets: WebRule[], guestCount: number): WebRule[] => {
    return timeSets
        .map((timeSet) => getWaitListTimeSet(timeSet, guestCount))
        .filter((timeSet) => timeSet.times.length > 0);
};

export {
    isTimeBookable,
    isTimeWaitListable,
    getBookableTimes,
    getWaitListTimes,
    getBookableTimeSets,
    getWaitListTimeSets,
};
